/* @refresh reset */
import { palette } from "lib/miolo";
import noop from "lodash/fp/noop";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import type { Descendant } from "slate";
import { createEditor } from "slate";
import type { ReactEditor, RenderLeafProps } from "slate-react";
import { Editable, Slate, withReact } from "slate-react";

interface FinancialHighlightsContentProps {
  content: Descendant[];
}

export default function FinancialHighlightsContent({ content }: FinancialHighlightsContentProps) {
  const editor = useMemo(() => withReact(createEditor() as ReactEditor), []);

  const renderElement = useCallback((props: any) => <Element {...props} />, []);
  // @ts-ignore
  const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);

  return (
    <Slate editor={editor} value={content} onChange={noop}>
      <Editable renderLeaf={renderLeaf} renderElement={renderElement} readOnly={true} />
    </Slate>
  );
}

export type ElementBlockType = "block-quote" | "bulleted-list" | "numbered-list" | "list-item" | string;

interface IElementProps {
  attributes: Record<string, any>;
  children: ReactNode;
  element: {
    type: ElementBlockType;
  };
}

function Element({ attributes, children, element }: IElementProps) {
  switch (element.type) {
    case "block-quote":
      return (
        <blockquote
          {...attributes}
          style={{
            padding: "8px 12px",
            // TODO(andreyvital): replace with palette colors
            borderLeft: `solid 2px ${palette.blueBayoux}`,
          }}
        >
          {children}
        </blockquote>
      );

    case "bulleted-list":
      return (
        <ul
          {...attributes}
          className="list-disc"
          style={{
            margin: 0,
            paddingLeft: 24,
          }}
        >
          {children}
        </ul>
      );

    case "numbered-list":
      return (
        <ol
          {...attributes}
          className="list-decimal"
          style={{
            paddingLeft: 24,
          }}
        >
          {children}
        </ol>
      );

    case "list-item":
      return <li {...attributes}>{children}</li>;

    default:
      return <p {...attributes}>{children}</p>;
  }
}

interface LeafProps {
  attributes: Record<string, any>;
  children: ReactNode;
  leaf: {
    bold?: boolean;
    italic?: boolean;
  };
}

function Leaf({ attributes, children, leaf }: LeafProps) {
  if (leaf.bold && leaf.italic) {
    return (
      <span {...attributes} className="font-bold italic">
        {children}
      </span>
    );
  }

  if (leaf.bold) {
    return (
      <span {...attributes}>
        <strong className="font-bold">{children}</strong>
      </span>
    );
  }

  if (leaf.italic) {
    return (
      <span {...attributes}>
        <em className="italic">{children}</em>
      </span>
    );
  }

  return <span {...attributes}>{children}</span>;
}
